import { useEffect, useMemo, useRef } from 'react'
import { useQueryClient } from 'react-query'
import { optimisticUpdateFileInInfiniteLists } from 'hooks/queries/files/cacheUtils'
import { useScheduledPostTargets } from 'hooks/queries/scheduledPostTargets/useScheduledPostTargets'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { ScheduledPostTarget } from 'types'
import { filesKeys } from 'hooks/queries/files/filesKeys'

const ACTIVE_STATUSES: Array<ScheduledPostTarget['status']> = ['queued', 'processing', 'retrying']

const targetsEqual = (nextTargets: ScheduledPostTarget[], currentTargets: ScheduledPostTarget[]) => {
  if (nextTargets.length !== currentTargets.length) return false

  const currentById = new Map(currentTargets.map((target) => [target.id, target]))

  return nextTargets.every((target) => {
    const current = currentById.get(target.id)
    if (!current) return false

    return (
      target.status === current.status &&
      target.updated_at === current.updated_at &&
      target.error_message === current.error_message &&
      target.post_url === current.post_url
    )
  })
}

interface PostTargetsPublishingSyncProps {
  fileKey: string
  scheduledPostId: number
  onComplete: () => void
  onActiveChange?: (isActive: boolean) => void
}

export const PostTargetsPublishingSync = ({
  fileKey,
  scheduledPostId,
  onComplete,
  onActiveChange,
}: PostTargetsPublishingSyncProps) => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  const hasSeenActiveRef = useRef(false)
  const hasCompletedRef = useRef(false)

  const { data: targets = [] } = useScheduledPostTargets(scheduledPostId, {
    enablePolling: true,
    enabled: !!workspaceId,
  })

  const hasActiveTargets = useMemo(() => targets.some((target) => ACTIVE_STATUSES.includes(target.status)), [targets])

  useEffect(() => {
    if (!onActiveChange) return
    if (targets.length === 0) return
    onActiveChange(hasActiveTargets)
  }, [hasActiveTargets, onActiveChange, targets.length])

  useEffect(() => {
    if (!workspaceId || targets.length === 0) return

    optimisticUpdateFileInInfiniteLists(queryClient, workspaceId, fileKey, (file) => {
      const currentTargets = file.scheduled_post_targets ?? []
      if (targetsEqual(targets, currentTargets)) {
        return file
      }

      return {
        ...file,
        scheduled_post_targets: targets,
      }
    })
  }, [targets, fileKey, workspaceId, queryClient])

  useEffect(() => {
    if (!workspaceId) return

    if (targets.length === 0) {
      onActiveChange?.(false)
      if (hasSeenActiveRef.current && !hasCompletedRef.current) {
        queryClient.invalidateQueries(filesKeys.partialInfinite(workspaceId))
        hasCompletedRef.current = true
        onComplete()
      }
      return
    }

    if (hasActiveTargets) {
      hasSeenActiveRef.current = true
      hasCompletedRef.current = false
      return
    }

    if (!hasCompletedRef.current) {
      onActiveChange?.(false)
      queryClient.invalidateQueries(filesKeys.partialInfinite(workspaceId))
      hasCompletedRef.current = true
      onComplete()
    }
  }, [targets, hasActiveTargets, workspaceId, queryClient, onComplete, onActiveChange])

  return null
}
